// @ts-strict-ignore
import fetch from "@lib/fetch";
import sharedConstants from "@lib/shared_constants.json";

import { integrationsDirectCdiImportSyncsAll } from "../integrationsDirectCdiImportSyncsAll/constants";
import { reactQueryFetcherAdapter, reactQueryMutationAdapter } from "../reactQueryFetcherAdapters";
import {
  postCdiTestConnectionCacheKey,
  createSyncCacheKey,
  getSyncCacheKey,
  listSyncsCacheKey,
  updateSyncCacheKey,
  postSnowflakeKeyRegenerateCacheKey,
  updateSyncStatusCacheKey,
  postSnowflakeDeleteKeyCacheKey,
  getSyncHistoryCacheKey,
  updateSnowflakeKeyCacheKey,
} from "./constants";
import type {
  GetCdiImportSyncsParams,
  ListCdiImportSyncsParams,
  CdiImportSyncsResponse,
  CdiTestConnectionResponse,
  PostSnowflakeImportKeyDeleteProps,
  GetSnowflakeImportSyncHistoryProps,
  CdiSyncHistoryResponse,
  CdiImportSync,
  CreateCdiImportSyncParams,
  DeleteCdiImportSyncParams,
  UpdateSnowflakeImportSyncStatusParams,
  CdiImportKey,
  UpdateCdiImportKeyProps,
  CdiErrorResponse,
  CdiKeyRegenerateProps,
  CdiKeyRegenerateResult,
  UpdateCdiImportSyncStatusResponse,
} from "./types";

export const createCdiImportSync = reactQueryMutationAdapter(
  [createSyncCacheKey, integrationsDirectCdiImportSyncsAll],
  (props: CreateCdiImportSyncParams): Promise<CdiImportSync> => {
    return fetch(
      "/integrations/direct/cdi/import-syncs",
      "POST",
      {
        app_group_id: props.appGroupId,
        ...props.importSync,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const updateCdiImportSync = reactQueryMutationAdapter(
  [listSyncsCacheKey, updateSyncCacheKey, integrationsDirectCdiImportSyncsAll],
  (props: CreateCdiImportSyncParams): Promise<CdiImportSync | CdiErrorResponse> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${props.importSync.integration_id}`,
      "PUT",
      {
        app_group_id: props.appGroupId,
        ...props.importSync,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const updateCdiImportSyncStatus = reactQueryMutationAdapter(
  [listSyncsCacheKey, updateSyncStatusCacheKey],
  (props: UpdateSnowflakeImportSyncStatusParams): Promise<UpdateCdiImportSyncStatusResponse> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${props.syncId}/status`,
      "PUT",
      {
        status: props.status,
        partner_name: props.partnerName,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const deleteCdiImportSync = reactQueryMutationAdapter(
  [listSyncsCacheKey, updateSyncCacheKey],
  (props: DeleteCdiImportSyncParams): Promise<null> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${props.syncId}`,
      "DELETE",
      {
        partner_name: props.partnerName,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const listCdiImportSyncs = reactQueryFetcherAdapter(
  listSyncsCacheKey,
  async (signal, { appGroupId, partnerName }: ListCdiImportSyncsParams): Promise<CdiImportSyncsResponse> => {
    return await fetch(
      "/integrations/direct/cdi/import-syncs",
      "GET",
      { app_group_id: appGroupId, partner_name: partnerName },
      { signal }
    );
  }
);

export const getCdiImportSync = reactQueryFetcherAdapter(
  getSyncCacheKey,
  async (signal, { syncId }: GetCdiImportSyncsParams): Promise<CdiImportSync> => {
    return await fetch(`/integrations/direct/cdi/import-syncs/${syncId}`, "GET", {}, { signal });
  }
);

export const postCdiTestConnection = reactQueryMutationAdapter(
  postCdiTestConnectionCacheKey,
  (syncId: string): Promise<CdiTestConnectionResponse> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${syncId}/connection`,
      "POST",
      {}, //params
      {}, //options
      { setContentType: false, propagateFetchRequestError: false } //settings
    );
  }
);

export const postCdiKeyRegenerate = reactQueryMutationAdapter(
  [postSnowflakeKeyRegenerateCacheKey, listSyncsCacheKey],
  (props: CdiKeyRegenerateProps): Promise<CdiKeyRegenerateResult> => {
    return fetch(
      `/integrations/direct/cdi/import-keys/${props.keyId}/regenerate`,
      "POST",
      { partner_name: props.partnerName },
      {}, //options
      { setContentType: true, propagateFetchRequestError: false } //settings);
    );
  }
);

export const postImportKeysDelete = reactQueryMutationAdapter(
  postSnowflakeDeleteKeyCacheKey,
  (props: PostSnowflakeImportKeyDeleteProps): Promise<any> => {
    return fetch(`/integrations/direct/cdi/import-keys/${props.keyId}/delete`, "POST", {
      partner_name: props.partnerName,
    });
  }
);

export const getImportSyncHistory = reactQueryFetcherAdapter(
  getSyncHistoryCacheKey,
  async (signal, props: GetSnowflakeImportSyncHistoryProps): Promise<CdiSyncHistoryResponse> => {
    return fetch(`/integrations/direct/cdi/import-syncs/${props.syncId}/sync_history`, "GET", props, {
      signal,
    });
  }
);

export const putImportKeysUpdate = reactQueryMutationAdapter(
  updateSnowflakeKeyCacheKey,
  (props: UpdateCdiImportKeyProps): Promise<CdiImportKey> => {
    if (props.partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery) {
      const body = new FormData();
      for (const key in props) {
        if (key === "import_key") {
          body.append(key, JSON.stringify(props[key]));
        } else {
          body.append(key, props[key]);
        }
      }
      return fetch(
        `/integrations/direct/cdi/import-keys/${props.keyId}/update`,
        "PUT",
        {},
        { body },
        { setContentType: false }
      );
    }
    return fetch(`/integrations/direct/cdi/import-keys/${props.keyId}/update`, "PUT", props);
  }
);
